
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ServiceType } from "@/types/booking";
import { Edit, Plus, Trash, Clock, DollarSign, Timer } from "lucide-react";
import { toast } from "sonner";

interface ServiceManagerProps {
  services: ServiceType[];
  onServicesChange: (services: ServiceType[]) => void;
}

export function ServiceManager({ services, onServicesChange }: ServiceManagerProps) {
  const [editMode, setEditMode] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<ServiceType>>({
    name: "",
    description: "",
    duration: 30,
    price: 0,
    waitlistEnabled: true,
    bufferTime: 0
  });
  
  const handleAddService = () => {
    // Trim and validate name
    const trimmedName = formData.name?.trim() || "";
    if (!trimmedName) {
      toast.error("Service name is required");
      return;
    }

    // Validate duration
    if (formData.duration === undefined || formData.duration === null) {
      toast.error("Duration is required");
      return;
    }
    if (formData.duration <= 0) {
      toast.error("Duration must be greater than 0 minutes");
      return;
    }
    if (formData.duration < 5) {
      toast.error("Duration must be at least 5 minutes");
      return;
    }
    if (formData.duration > 480) {
      toast.error("Duration cannot exceed 480 minutes (8 hours)");
      return;
    }
    if (!Number.isInteger(formData.duration)) {
      toast.error("Duration must be a whole number");
      return;
    }

    // Validate price
    if (formData.price === undefined || formData.price === null) {
      toast.error("Price is required");
      return;
    }
    if (formData.price < 0) {
      toast.error("Price cannot be negative");
      return;
    }
    if (formData.price > 10000) {
      toast.error("Price cannot exceed $10,000");
      return;
    }

    // Validate buffer time
    const bufferTime = formData.bufferTime || 0;
    if (bufferTime < 0) {
      toast.error("Buffer time cannot be negative");
      return;
    }
    if (bufferTime > 240) {
      toast.error("Buffer time cannot exceed 240 minutes (4 hours)");
      return;
    }
    if (!Number.isInteger(bufferTime)) {
      toast.error("Buffer time must be a whole number");
      return;
    }

    const newService: ServiceType = {
      id: crypto.randomUUID(),
      name: trimmedName,
      description: formData.description?.trim() || "",
      duration: formData.duration,
      price: formData.price,
      waitlistEnabled: formData.waitlistEnabled,
      bufferTime: bufferTime
    };

    onServicesChange([...services, newService]);

    // Reset form
    setFormData({
      name: "",
      description: "",
      duration: 30,
      price: 0,
      waitlistEnabled: true,
      bufferTime: 0
    });

    toast.success("Service added successfully");
  };
  
  const handleEditService = () => {
    if (!editMode) {
      return;
    }

    // Trim and validate name
    const trimmedName = formData.name?.trim() || "";
    if (!trimmedName) {
      toast.error("Service name is required");
      return;
    }

    // Validate duration
    if (formData.duration === undefined || formData.duration === null) {
      toast.error("Duration is required");
      return;
    }
    if (formData.duration <= 0) {
      toast.error("Duration must be greater than 0 minutes");
      return;
    }
    if (formData.duration < 5) {
      toast.error("Duration must be at least 5 minutes");
      return;
    }
    if (formData.duration > 480) {
      toast.error("Duration cannot exceed 480 minutes (8 hours)");
      return;
    }
    if (!Number.isInteger(formData.duration)) {
      toast.error("Duration must be a whole number");
      return;
    }

    // Validate price
    if (formData.price === undefined || formData.price === null) {
      toast.error("Price is required");
      return;
    }
    if (formData.price < 0) {
      toast.error("Price cannot be negative");
      return;
    }
    if (formData.price > 10000) {
      toast.error("Price cannot exceed $10,000");
      return;
    }

    // Validate buffer time
    const bufferTime = formData.bufferTime || 0;
    if (bufferTime < 0) {
      toast.error("Buffer time cannot be negative");
      return;
    }
    if (bufferTime > 240) {
      toast.error("Buffer time cannot exceed 240 minutes (4 hours)");
      return;
    }
    if (!Number.isInteger(bufferTime)) {
      toast.error("Buffer time must be a whole number");
      return;
    }

    const updatedServices = services.map(service =>
      service.id === editMode
        ? {
            ...service,
            name: trimmedName,
            description: formData.description?.trim() || "",
            duration: formData.duration,
            price: formData.price,
            waitlistEnabled: formData.waitlistEnabled,
            bufferTime: bufferTime
          }
        : service
    );

    onServicesChange(updatedServices);
    setEditMode(null);

    // Reset form
    setFormData({
      name: "",
      description: "",
      duration: 30,
      price: 0,
      waitlistEnabled: true,
      bufferTime: 0
    });

    toast.success("Service updated successfully");
  };
  
  const handleRemoveService = (id: string) => {
    onServicesChange(services.filter(service => service.id !== id));
    toast.success("Service removed");
  };
  
  const startEditing = (service: ServiceType) => {
    setEditMode(service.id);
    setFormData({
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      waitlistEnabled: service.waitlistEnabled !== false,
      bufferTime: service.bufferTime || 0
    });
  };
  
  const cancelEditing = () => {
    setEditMode(null);
    setFormData({
      name: "",
      description: "",
      duration: 30,
      price: 0,
      waitlistEnabled: true,
      bufferTime: 0
    });
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Services</CardTitle>
        </CardHeader>
        <CardContent>
          {services.length > 0 ? (
            <div className="space-y-4">
              {services.map(service => (
                <div 
                  key={service.id}
                  className="p-4 border rounded-lg flex justify-between items-start"
                >
                  <div>
                    <h3 className="font-medium">{service.name}</h3>
                    <p className="text-sm text-muted-foreground">{service.description}</p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {service.duration} min
                      </span>
                      <span className="flex items-center font-semibold">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {service.price}
                      </span>
                      {service.bufferTime > 0 && (
                        <span className="flex items-center">
                          <Timer className="h-3 w-3 mr-1" />
                          {service.bufferTime} min buffer
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm">
                      <span>Same-day waitlist: {service.waitlistEnabled !== false ? "Enabled" : "Disabled"}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEditing(service)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveService(service.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <p className="text-muted-foreground">No services added yet</p>
            </div>
          )}
          
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-medium mb-4">
              {editMode ? "Edit Service" : "Add New Service"}
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="serviceName">Service Name*</Label>
                <Input 
                  id="serviceName" 
                  placeholder="e.g. Classic Haircut"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="serviceDescription">Description</Label>
                <Textarea 
                  id="serviceDescription" 
                  placeholder="Brief description of the service"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="serviceDuration">Duration (minutes)*</Label>
                  <Input 
                    id="serviceDuration" 
                    type="number"
                    placeholder="30"
                    value={formData.duration === 0 ? "" : formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || 0})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="servicePrice">Price ($)*</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input 
                      id="servicePrice" 
                      type="number"
                      className="pl-8"
                      placeholder="25"
                      value={formData.price === 0 ? "" : formData.price}
                      onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>
              
              {/* Add buffer time field */}
              <div className="space-y-2">
                <Label htmlFor="bufferTime">Buffer Time (minutes)</Label>
                <div className="flex items-center">
                  <Input 
                    id="bufferTime" 
                    type="number"
                    placeholder="0"
                    value={formData.bufferTime === 0 ? "" : formData.bufferTime}
                    onChange={(e) => setFormData({...formData, bufferTime: parseInt(e.target.value) || 0})}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Time needed between appointments for cleanup or preparation
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="waitlistEnabled" 
                  checked={formData.waitlistEnabled !== false}
                  onCheckedChange={(checked) => setFormData({...formData, waitlistEnabled: checked})}
                />
                <Label htmlFor="waitlistEnabled">Enable same-day waitlist for this service</Label>
              </div>
              
              <div className="flex justify-end space-x-2">
                {editMode && (
                  <Button 
                    variant="outline" 
                    onClick={cancelEditing}
                  >
                    Cancel
                  </Button>
                )}
                
                <Button 
                  onClick={editMode ? handleEditService : handleAddService}
                  className="bg-figgy-500 hover:bg-figgy-600"
                >
                  {editMode ? (
                    "Update Service"
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Service
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
